import styled, { css } from 'styled-components';
import { Typography } from '../../global/theme';
import { mobile_xl, tablet } from '../../global/mediaQueryMixin';
import { AlignCenter, HideScrollbar, LineClamp, ShowVerticalScrollbar } from '../../global/commonStyle';

interface TableToolbarContainerProps {
  top: number;
  left: number;
  isMobile: boolean;
}

interface Props {
  isMessageInputBox?: boolean;
}

const TiptapMain = styled.div<Props>`
  position: relative;
  &.overflow-hidden {
    overflow: hidden;
  }

  .e-rte-video .e-video-inline {
    width: 355px;
  }

  .text-block {
    display: block;
    color: var(--text-primary);
    ${Typography.body_md}
  }
  .text-block > img {
    max-width: 100%;
    max-height: 100%;
    width: 300px;
    height: auto;
  }

  .text {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--space-12);
    color: var(--text-primary);
    ${Typography.body_md}
    .embed-icon {
      max-width: 14px;
      max-height: 14px;
      width: 14px;
      height: 14px;
      margin-top: var(--space-0);
      border: unset;
    }
    p {
      ${LineClamp(2)}
      text-overflow: ellipsis;
      margin: var(--space-0);
      padding: var(--space-0);
      ${Typography.body_md_medium}
    }
    img {
      max-width: 300px;
      max-height: 300px;
      width: auto;
      height: auto;
      margin: var(--space-0) auto;
      border: 0.5px solid var(--border-primary);
      border-radius: var(--border-radius-8);
      ${mobile_xl(css`
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
      `)}
    }
  }
  .e-rte-image.e-imginline {
    vertical-align: baseline;
  }
  .e-rte-video.e-video-inline {
    max-width: 354px;
  }

  .thread-div {
    padding: var(--space-8);
    border-radius: var(--border-radius-8);
    border: 0.5px solid var(--border-primary);
    background: var(--background-tertiary);
    width: 100%;
    /* Default to content-sized reply bubble */
    max-width: fit-content;
  }

  /* When reply content contains a table, allow it to expand within the message input box */
  ${({ isMessageInputBox }) =>
    isMessageInputBox &&
    css`
      .thread-div:has(.tableWrapper) {
        max-width: calc(100% - 17px);
      }
    `}
  .content {
    display: flex;
    flex-direction: row;
    gap: var(--space-10);
  }
  .line {
    width: 1px;
    background-color: var(--text-placeholder);
    border: 0.5px solid var(--text-placeholder);
    height: auto;
    border-radius: var(--border-radius-4);
  }
  .right-div {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    overflow: auto;
  }
  .header {
    display: flex;
    flex-direction: row;
    gap: var(--space-12);
    justify-content: space-between;
  }
  .left-header {
    ${AlignCenter}
    gap: var(--space-8);
  }
  .name {
    color: var(--text-primary);
    ${Typography.body_md}
  }
  .time {
    color: var(--text-secondary);
    ${Typography.body_md}
  }

  .copy-icon {
    width: 18px;
    height: 18px;
    cursor: pointer;
    background: var(--background-secondary);
    display: none;
  }

  /* Special margin for first table in the editor */
  .tiptap > div > .tableWrapper:first-child,
  .tiptap > .tableWrapper:first-child {
    margin-top: var(--space-32);
  }
  .tiptap > p:first-child {
    margin: var(--space-0);
  }

  ${mobile_xl(css<Props>`
    .e-rte-image.e-imginline {
      padding: var(--space-0);
    }

    .text {
      word-break: break-all;
      p {
        img {
          width: 100%;
          border-radius: var(--border-radius-4);
        }
      }
    }
    .header {
      gap: var(--space-12);
    }
    .thread-div {
      max-width: -webkit-fill-available;
      border-radius: ${({ isMessageInputBox }) =>
        isMessageInputBox ? 'var(--border-radius-12)' : 'var(--border-radius-8)'};
    }
    .right-div {
      gap: var(--space-4);
      width: 100%;
    }

    .cleanshot-video-main-div {
      max-width: -webkit-fill-available;
    }

    .e-rte-video.e-video-inline {
      max-width: 100%;
    }
    .right-div {
      gap: var(--space-4);
      word-break: break-all;
    }
    .left-header {
      gap: var(--space-3);
    }
    .document-embed-container {
      padding: var(--space-8);
      gap: var(--space-8);
    }
    .document-content {
      min-width: 0;
    }
    .file-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `)}
`;

const TableMainDiv = styled.div`
  display: flex;
  gap: var(--space-8);
  max-width: 406px;
  background-color: var(--background-primary);
  border: 0.5px solid var(--border-primary);
  box-shadow: var(--shadow-modal);
  padding: var(--space-4) var(--space-4) var(--space-4) var(--space-12);
  border-radius: var(--border-radius-8);
  position: absolute;
  /* z-index: var(--z-index-99); */
  pointer-events: all;
  ${mobile_xl(css`
    width: calc(100vw - 58px);
    margin-left: var(--space-3);
    ${HideScrollbar}
  `)}
`;

const ButtonMainDiv = styled.div`
  ${AlignCenter}
  gap: var(--space-4);
  pointer-events: all;
  &.header {
    svg path:nth-of-type(1) {
      fill: var(--neutral-primary);
    }
  }
  p {
    margin: var(--space-0);
    ${Typography.body_md}
    color: var(--text-secondary);
  }
`;

const Divider = styled.div`
  border: 0.5px solid var(--border-primary);
`;

const TipTapWrapperDiv = styled.div<{ applyPosition?: boolean; isMessageInputBox?: boolean }>`
  position: relative;
  .button-div {
    display: flex;
    gap: var(--space-8);
    justify-content: flex-end;
  }
  ${({ applyPosition }) =>
    applyPosition &&
    css`
      position: relative;
    `}
  ${({ isMessageInputBox }) =>
    isMessageInputBox &&
    css`
      ${mobile_xl(
        css`
          width: 100%;
        `
      )}
    `}
`;

const LineDiv = styled.div`
  border: 0.5px solid var(--text-muted);
  height: 18px;
`;

const ImproveModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: var(--z-index-toast);
`;

const ImproveModal = styled.div<{ loading?: boolean }>`
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  background: var(--background-primary);
  box-shadow: var(--shadow-modal);
  border: 0.5px solid var(--border-primary);
  border-radius: var(--border-radius-12);
  padding: var(--space-8) var(--space-8) var(--space-8) var(--space-12);
  min-width: 500px;
  max-width: 500px;
  max-height: calc(100vh - 16px);
  overflow: hidden;
  ${tablet(css`
    min-width: calc(100vw - 304px);
    max-width: calc(100vw - 304px);
  `)}
  ${mobile_xl(css`
    min-width: calc(100vw - 60px);
    max-width: calc(100vw - 60px);
  `)}
  .button-div {
    display: flex;
    gap: var(--space-8);
    justify-content: flex-end;
    flex-shrink: 0;
  }
  textarea {
    outline: none;
    resize: none;
    border: none;
    ${Typography.body_md}
    color: ${({ loading }) => (loading ? 'var(--text-secondary)' : 'var(--text-primary)')};
    padding: var(--space-0);
    max-height: 120px;
    font-family: unset;
    background-color: transparent;
    ${ShowVerticalScrollbar}
    &:focus {
      outline: none;
    }
  }
`;

const TableToolbarContainer = styled.div<TableToolbarContainerProps>`
  position: absolute;
  top: ${({ top }) => top}px;
  left: ${({ isMobile, left }) => (isMobile ? '-3px' : `${left}px`)};
  transform: translateX(-50%);
  z-index: var(--z-index-header);
`;

const DropdownWrapper = styled.div<{ top?: number; left?: number }>`
  position: fixed;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  z-index: var(--z-index-highest);
  padding: var(--space-4);
  background-color: var(--background-primary);
  border: 0.5px solid var(--border-primary);
  box-shadow: var(--shadow-modal);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  border-radius: var(--border-radius-8);
  width: auto;
  min-width: 116px;
`;

const DropdownButton = styled.button`
  padding: var(--space-4) var(--space-6);
  background: none;
  border: none;
  text-align: left;
  width: 100%;
  cursor: pointer;
  transition: background-color var(--transition-normal);
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: var(--text-primary);
  border-radius: var(--border-radius-4);

  /* font for mobile screen  */
  @media (max-width: 449px) {
    font-size: 15px;
    line-height: 22px;
  }

  &:hover {
    background-color: var(--neutral-secondary);
  }
`;

export {
  TiptapMain,
  TableMainDiv,
  ButtonMainDiv,
  Divider,
  TipTapWrapperDiv,
  LineDiv,
  ImproveModalBackdrop,
  ImproveModal,
  TableToolbarContainer,
  DropdownWrapper,
  DropdownButton
};
